/**
 * REGISTRO ÚNICO DE TELAS do Sentinela — UMA fonte para: o menu (barra lateral e gaveta), a busca
 * "Ir para…", a trilha da barra superior, o `<title>` de cada rota, o "Sem acesso" e o título/eyebrow do
 * `CabecalhoDaPagina`. Tela nova entra aqui e nasce certa nos seis lugares.
 *
 * ─── Por que existe (auditorias de 11/10/2026: SEN-07/08/10/30 e NAV-07/10/12/30) ───────────────────────
 * - O menu tinha 35 itens em 5 seções por "produto interno"; o NOC (papel que mais usa o painel) via 5
 *   portas que a API fecha com 403 (Forense, Ofícios, Auditoria, Usuários, Configurações), e só o
 *   Financeiro era filtrado — com uma regra escrita à mão (`to === "/admin/circuitos"`).
 * - Agora o menu é agrupado por TAREFA (6 grupos de 3 a 7 itens + Dashboard solto) e cada item diz QUAL
 *   PERMISSÃO DO SERVIDOR a tela exige (`permissoes`); `papeis` sai de `PERMISSOES` do pacote
 *   `@sentinela/shared` — o mesmo mapa que `requerPermissao` usa nas rotas. NÃO copiar a lista de papéis
 *   à mão: foi exatamente assim que o NAV-07 nasceu. O teste `menu.test.ts` cruza este arquivo com as
 *   rotas da API (lê `requerPermissao` do código-fonte da API) e falha se uma tela passar a exigir outra
 *   permissão.
 * - URLs NÃO mudam (favoritos, e-mails e cartas seguem valendo): só rótulo, grupo e ordem. Para uma tela
 *   que muda de nome, `sinonimos` mantém a busca achando o nome antigo.
 *
 * ─── Decisões registradas ───────────────────────────────────────────────────────────────────────────
 * - "Auditoria" (sistema) virou "Registro de ações" (D3 do relatório de navegação, aprovada em
 *   11/10/2026): havia duas "Auditoria" no menu ("Auditoria de switches" é outra coisa). A URL segue
 *   `/admin/auditoria`; a busca acha por "auditoria".
 * - O Financeiro enxerga SÓ "Circuitos" (decisão antiga, ver `PERMISSOES["circuitos.bloquear"]`). Mas
 *   `circuitos.ver` também o deixaria abrir "Circuitos fora do ar" e "Manutenções" no servidor; esses dois
 *   itens trazem `soPapeis` para continuar fora do menu dele. `soPapeis` só pode ESTREITAR o que a
 *   permissão dá, nunca ampliar (o teste confere).
 * - Ícone: um por item, nunca repetido (o `AlertTriangle` servia a "Origens desconhecidas" e a "Top
 *   ofensores"). O teste confere.
 * - Rótulos em frase (só a primeira maiúscula; guia de marca v1.3), até 24 caracteres.
 */
import {
  BookOpen,
  Boxes,
  Cable,
  CalendarClock,
  CircleHelp,
  ClipboardCheck,
  Crosshair,
  Fingerprint,
  FileClock,
  Gauge,
  Gavel,
  Globe,
  History,
  Inbox,
  LayoutDashboard,
  Link2,
  Mail,
  MapPin,
  Network,
  Newspaper,
  Radio,
  Repeat,
  Router,
  ScrollText,
  Search,
  Server,
  Settings,
  ShieldAlert,
  ShieldCheck,
  ShieldHalf,
  TrendingDown,
  UserCog,
  Users,
  Wifi,
  Zap,
} from "lucide-react";
import { PERMISSOES, ROLE_LABEL, type Permissao, type Role } from "@sentinela/shared/permissoes";
import {
  itemAtivo,
  grupoDoItem,
} from "../components/shell/registro.js";
import type { GrupoNav, ItemNav } from "../components/shell/tipos.js";

export const GRUPOS: readonly GrupoNav[] = [
  { id: "seguranca", rotulo: "Segurança" },
  { id: "clientes", rotulo: "Clientes e avisos" },
  { id: "circuitos", rotulo: "Circuitos" },
  { id: "equipamentos", rotulo: "Equipamentos" },
  { id: "rede", rotulo: "Serviços de rede" },
  { id: "sistema", rotulo: "Sistema" },
];

/** Papéis que a MATRIZ DO SERVIDOR deixa passar em TODAS as permissões listadas (interseção). */
export function papeisDe(permissoes: readonly Permissao[]): readonly Role[] {
  const [primeira, ...resto] = permissoes;
  if (!primeira) return [];
  return (PERMISSOES[primeira] as readonly Role[]).filter((r) => resto.every((p) => (PERMISSOES[p] as readonly Role[]).includes(r)));
}

type Def = Omit<ItemNav, "papeis" | "permissoes"> & {
  /** Permissão(ões) que a ROTA DE ENTRADA da tela exige na API (`requerPermissao`). */
  permissoes: readonly Permissao[];
  /** Estreita (nunca amplia) os papéis dados pela permissão. Ver o comentário do arquivo (Financeiro). */
  soPapeis?: readonly Role[];
  /** Só para tela SEM chamada à API (ex.: Manual): papéis explícitos, no lugar de `permissoes`. */
  papeisSemApi?: readonly Role[];
};

const TODOS_MENOS_FINANCEIRO: readonly Role[] = ["master", "seguranca_admin", "noc", "compliance", "auditor"];

function item(d: Def): ItemNav {
  const { permissoes, soPapeis, papeisSemApi, ...base } = d;
  const doServidor = permissoes.length ? papeisDe(permissoes) : papeisSemApi ?? [];
  const papeis = soPapeis ? doServidor.filter((r) => soPapeis.includes(r)) : doServidor;
  return { ...base, permissoes, papeis };
}

export const ITENS: readonly ItemNav[] = [
  // ---- topo
  item({ id: "dashboard", rotulo: "Dashboard", icone: LayoutDashboard, href: "/admin", grupo: null, exato: true, permissoes: ["alertas.ver"], sinonimos: ["início", "inicio", "painel", "home", "kpi"] }),

  // ---- Segurança
  item({ id: "alertas", rotulo: "Alertas", icone: ShieldAlert, href: "/admin/alertas", grupo: "seguranca", permissoes: ["alertas.ver"], sinonimos: ["incidentes", "cert.br", "sla"] }),
  item({ id: "ddos", rotulo: "DDoS inbound", icone: Zap, href: "/admin/ddos-inbound", grupo: "seguranca", permissoes: ["alertas.ver"], sinonimos: ["ataque", "akvorado", "negação de serviço"] }),
  item({ id: "ioc", rotulo: "Banco IOC", icone: Fingerprint, href: "/admin/ioc", grupo: "seguranca", permissoes: ["alertas.ver"], sinonimos: ["indicadores", "ameaças", "reputação"] }),
  item({ id: "verificacoes", rotulo: "Verificações", icone: ShieldCheck, href: "/admin/verificacoes", grupo: "seguranca", permissoes: ["alertas.ver"], sinonimos: ["resolução", "probe", "confirmação"] }),
  item({ id: "top-ofensores", rotulo: "Top ofensores", icone: Crosshair, href: "/admin/top-ofensores", grupo: "seguranca", permissoes: ["clientes.ver"], sinonimos: ["ranking", "reincidência", "score"] }),
  item({ id: "forense", rotulo: "Forense CGNAT", icone: Search, href: "/admin/forense", grupo: "seguranca", permissoes: ["forense.ver"], sinonimos: ["cgnat", "investigação", "porta", "ip público"] }),
  item({ id: "oficios", rotulo: "Ofícios judiciais", icone: Gavel, href: "/admin/oficios", grupo: "seguranca", permissoes: ["forense.ver"], sinonimos: ["justiça", "ordem judicial", "polícia"] }),

  // ---- Clientes e avisos
  item({ id: "clientes", rotulo: "Clientes", icone: Users, href: "/admin/clientes", grupo: "clientes", permissoes: ["clientes.ver"], sinonimos: ["cadastro", "contrato", "ixc"] }),
  item({ id: "vinculos", rotulo: "Vínculos IP", icone: Link2, href: "/admin/vinculos", grupo: "clientes", permissoes: ["clientes.ver"], sinonimos: ["ip sem dono", "pendentes", "phpipam"] }),
  item({ id: "historico-ip", rotulo: "Histórico de IP", icone: History, href: "/admin/historico-ip", grupo: "clientes", permissoes: ["clientes.ver"], sinonimos: ["quem tinha o ip", "endereço"] }),
  item({ id: "avisos", rotulo: "Avisos emitidos", icone: Mail, href: "/admin/cartas/pendentes", grupo: "clientes", permissoes: ["cartas.ver"], sinonimos: ["cartas", "notificações", "aprovar carta"], rotasFilhas: ["/admin/cartas"] }),
  item({ id: "emails", rotulo: "E-mails recebidos", icone: Inbox, href: "/admin/emails", grupo: "clientes", permissoes: ["alertas.ver"], sinonimos: ["gmail", "caixa", "cert"] }),
  item({ id: "trocas", rotulo: "Trocas de prefixo", icone: Repeat, href: "/admin/trocas-prefixo", grupo: "clientes", permissoes: ["trocas.ver"], sinonimos: ["renumeração", "prefixo", "cidr"] }),

  // ---- Circuitos
  item({ id: "circuitos", rotulo: "Circuitos", icone: Cable, href: "/admin/circuitos", grupo: "circuitos", exato: true, permissoes: ["circuitos.ver"], sinonimos: ["inventário", "bloqueio", "desbloqueio", "vlan", "link"] }),
  item({ id: "circuitos-fora", rotulo: "Circuitos fora do ar", icone: TrendingDown, href: "/admin/circuitos/down", grupo: "circuitos", permissoes: ["circuitos.ver"], soPapeis: TODOS_MENOS_FINANCEIRO, sinonimos: ["quedas", "down", "indisponível", "aviso de queda"] }),
  item({ id: "manutencoes", rotulo: "Manutenções", icone: CalendarClock, href: "/admin/manutencoes", grupo: "circuitos", permissoes: ["circuitos.ver"], soPapeis: TODOS_MENOS_FINANCEIRO, sinonimos: ["janela", "programada", "gerência pró-ativa", "emergencial"] }),

  // ---- Equipamentos
  item({ id: "infra-dashboard", rotulo: "Dashboard infra", icone: Gauge, href: "/admin/infra/dashboard", grupo: "equipamentos", permissoes: ["infra.dashboard"], sinonimos: ["saúde da rede", "bgp", "alarmes"] }),
  item({ id: "equipamentos", rotulo: "Equipamentos", icone: Server, href: "/admin/infra/devices", grupo: "equipamentos", permissoes: ["infra.ver"], sinonimos: ["devices", "inventário", "catálogo", "marcas", "modelos"], rotasFilhas: ["/admin/infra/catalogo"] }),
  item({ id: "datacom", rotulo: "Switch Datacom", icone: Boxes, href: "/admin/infra/datacom", grupo: "equipamentos", permissoes: ["infra.ver"], sinonimos: ["dm"] }),
  item({ id: "huawei", rotulo: "Switch Huawei", icone: Network, href: "/admin/infra/huawei", grupo: "equipamentos", permissoes: ["infra.ver"], sinonimos: ["s6730", "vrp"] }),
  item({ id: "roteadores", rotulo: "Roteadores / BNG", icone: Router, href: "/admin/infra/roteadores", grupo: "equipamentos", permissoes: ["infra.ver"], sinonimos: ["bng", "cisco", "asr"] }),
  item({ id: "firewall", rotulo: "Firewall ASA", icone: ShieldHalf, href: "/admin/infra/firewall", grupo: "equipamentos", permissoes: ["infra.ver"], sinonimos: ["asa", "cisco"] }),

  // ---- Serviços de rede
  // PPPoE liga assinante, IP e MAC (classe CGNAT): a rota exige `forense.consultar_tecnico` (master, admin
  // de segurança e NOC), NÃO `infra.ver`. O menu antigo mostrava a tela a compliance e auditor, que levavam 403.
  item({ id: "pppoe", rotulo: "Sessões PPPoE", icone: Wifi, href: "/admin/infra/sessoes", grupo: "rede", permissoes: ["forense.consultar_tecnico"], sinonimos: ["autenticação", "login", "assinante conectado"] }),
  item({ id: "origens", rotulo: "Origens desconhecidas", icone: CircleHelp, href: "/admin/infra/origens-desconhecidas", grupo: "rede", permissoes: ["infra.ver"], sinonimos: ["syslog", "equipamento não cadastrado"] }),
  item({ id: "logs", rotulo: "Logs", icone: ScrollText, href: "/admin/infra/logs", grupo: "rede", permissoes: ["infra.ver"], sinonimos: ["syslog", "eventos", "link down"] }),
  item({ id: "probe", rotulo: "Histórico do probe", icone: Radio, href: "/admin/probe-historico", grupo: "rede", permissoes: ["alertas.ver"], sinonimos: ["nmap", "varredura", "tcp", "udp"] }),
  item({ id: "auditoria-switches", rotulo: "Auditoria de switches", icone: ClipboardCheck, href: "/admin/infra/auditoria", grupo: "rede", permissoes: ["infra.ver"], sinonimos: ["conformidade", "configuração", "comandos"] }),
  item({ id: "dns", rotulo: "DNS Manager", icone: Globe, href: "/admin/dns", grupo: "rede", permissoes: ["dns.ver"], sinonimos: ["ptr", "reverso", "zona"] }),
  item({ id: "geofeed", rotulo: "Geofeed RFC 8805", icone: MapPin, href: "/admin/geofeed", grupo: "rede", permissoes: ["geofeed.ver"], sinonimos: ["geolocalização", "registro.br", "coordenada"] }),

  // ---- Sistema (Usuários, Configurações, Registro de ações, Manual, ChangeLog — ChangeLog sempre por último)
  item({ id: "usuarios", rotulo: "Usuários", icone: UserCog, href: "/admin/usuarios", grupo: "sistema", permissoes: ["usuarios.ver"], sinonimos: ["acessos", "papéis", "permissões", "sessões"] }),
  item({ id: "configuracoes", rotulo: "Configurações", icone: Settings, href: "/admin/configuracoes", grupo: "sistema", permissoes: ["configuracoes.ver"], sinonimos: ["chaves", "kill switch", "ajustes", "parâmetros"] }),
  item({ id: "registro-de-acoes", rotulo: "Registro de ações", icone: FileClock, href: "/admin/auditoria", grupo: "sistema", permissoes: ["auditoria.ver"], sinonimos: ["auditoria", "quem fez o quê", "log de ações", "trilha"] }),
  // Manual e docs não chama a API: os papéis são os que o menu já mostrava (todos, menos o Financeiro, que
  // entra só para Circuitos). É a ÚNICA tela sem permissão do servidor; o teste exige essa justificativa.
  item({ id: "manual", rotulo: "Manual e docs", icone: BookOpen, href: "/admin/manual", grupo: "sistema", permissoes: [], papeisSemApi: TODOS_MENOS_FINANCEIRO, sinonimos: ["ajuda", "documentação", "runbook", "como usar"] }),
  // ChangeLog é aberto a todos os papéis @usetelecom (transparência operacional); só master edita. O
  // servidor é a fonte da verdade (changelog.ver/editar).
  item({ id: "changelog", rotulo: "ChangeLog", icone: Newspaper, href: "/admin/changelog", grupo: "sistema", permissoes: ["changelog.ver"], sinonimos: ["novidades", "versões", "o que mudou", "histórico de mudanças"] }),
];

/** Rótulo do papel para a pessoa (vem do pacote compartilhado, não é redigitado aqui). */
export function rotuloDoPapel(papel: string): string {
  return (ROLE_LABEL as Record<string, string>)[papel] ?? papel;
}

/** A tela e o grupo da rota (prefixo mais longo vence). Usado pelo `CabecalhoDaPagina`. */
export function itemDoMenu(pathname: string): { item: ItemNav; grupo: GrupoNav | null } | null {
  const it = itemAtivo(ITENS, pathname);
  return it ? { item: it, grupo: grupoDoItem(it, GRUPOS) } : null;
}
